/* eslint-disable no-extra-parens */
import { Card, Col, Row } from "react-bootstrap";
import PropTypes from "prop-types";
import { useMemo } from "react";

import AccessToFeelingsAssessment from "../components/AccessToFeelingsAssessment";
import EmotionAccess from "../components/EmotionAccess";
import EmotionBadge from "../components/EmotionBadge";
import EmotionDescription from "../components/EmotionDescription";
import EmotionPercentile from "../components/EmotionPercentile";
import EqqoPieChart from "../../EqqoPieChart";
import NextPrevLinks from "../../components/NextPrevLinks";
import ScaleBarChart from "../components/ScaleBarChart";
import SectionIntroduction from "../components/SectionIntroduction";
import { emotionToFields } from "../helpers/reportHelpers";
import { useReport } from "../ReportContext";
import { useUser } from "../../UserContext";


const Emotions = ({ showHeader }) => {
	const { reportContent: { acf: { emotions, summary } } } = useReport();
	const { respondent, respondentLoading } = useUser();

	const stats = useMemo(() => respondent?.weqProfile?.stats, [respondent]);
	const percentiles = useMemo(() => respondent?.weqProfile?.percentiles, [respondent]);

	return respondentLoading
		? null
		: (
			<>
				{showHeader ? <><h1>Your Emotions</h1><hr /></> : null}
				<div className="report-view">
					<SectionIntroduction
						text={emotions.introduction}
						videoUrl={`https://fast.wistia.net/embed/iframe/qutl3who93`}
					/>

					<hr />

					<h2>Information about your Access to Range of Feelings </h2>
					
					<Card body className="bg-white my-4">
						<ScaleBarChart value={percentiles?.feelings_Access} />
					</Card>

					<AccessToFeelingsAssessment />

					<hr />

					<h2 className="print-page-break-before">Summary of Your Emotions</h2>

					<Card body className="bg-white my-4 emotions-charts">
						<div dangerouslySetInnerHTML={{ __html: summary?.emotions?.pie_chart }} />
						<EqqoPieChart
							className="d-none d-lg-block"
							scores={[
								stats?.fANG_dist,
								stats?.fANX_dist,
								stats?.fEA_dist,
								stats?.fJO_dist,
								stats?.fLO_dist,
								stats?.fSA_dist,
								stats?.fSHA_dist
							]}
						/>

						<Row className="d-lg-none d-md-flex d-sm-flex" md={2} sm={2}>
							{
								["Anger", "Anxiety", "Fear", "Joy", "Love", "Sadness", "Shame"].map(emotion => (
									<Col className="pb-3" key={emotion} style={{ maxWidth: "50%", minWidth: "50%" }}>
										<EmotionBadge emotion={emotion} />
									</Col>
								))
							}
						</Row>
						<hr />
						<div dangerouslySetInnerHTML={{ __html: summary?.emotions?.compared_to_others }} />
						
						{
							Object.entries(emotionToFields).map(([emotion, stat]) => (
								<div key={emotion}>
									<EmotionPercentile emotion={emotion} percentile={percentiles?.[`${stat}_dist`] || 0} />
									{emotion === 'Shame' ? null : <hr />}
								</div>
							))
						}
					</Card>
					
					{
						Object.entries(emotionToFields).map(([emotion]) => <EmotionAccess emotion={emotion} key={emotion} />)
					}

					<hr />

					<h2 className="print-page-break-before">Explore the information and gift of each emotion below</h2>

					<EmotionDescription emotion="Anger" />
					<EmotionDescription emotion="Anxiety" />
					<EmotionDescription emotion="Fear" />
					<EmotionDescription emotion="Joy" />
					<EmotionDescription emotion="Love" />
					<EmotionDescription emotion="Sadness" />
					<EmotionDescription emotion="Shame" />

					<NextPrevLinks className="mt-4" />
				</div>
			</>
		);
};

Emotions.propTypes = {
	showHeader: PropTypes.bool
};

Emotions.defaultProps = {
	showHeader: false
}

export default Emotions;
